import React, { useEffect, useInsertionEffect, useLayoutEffect, useState } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import Login from 'containers/Login/index';
import Register from 'containers/Register/index';
import AuthLayout from 'layouts/AuthLayout/AuthLayout';
import MainLayout from 'layouts/MainLayout/index';
import { hasValidStoredSession, useUserActions } from 'actions/user';
import { instance } from 'utils/request';
import { CopyToastProvider } from 'contexts/CopyToastContext';
import './App.scss';
import MainContent from 'layouts/MainLayout/MainContent';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks/appSelector';
import {
  ADMINISTRATION_TOOLKIT_SEGMENT,
  getUnauthenticatedLoginTarget,
  getLogoutLoginPath,
  INSPECTIONS_TOOLKIT_SEGMENT,
  LOGIN_PORTAL_SLUGS,
  syncActiveSectorFromPathname,
} from 'config/loginPortals';

const getRoutes = (isLoggedIn: boolean, pathname: string) => {
  const unauthenticatedLoginTarget = getUnauthenticatedLoginTarget(pathname);

  return [
  {
    path: '/',
    element: isLoggedIn ? (
      <MainLayout />
    ) : (
      <Navigate
        to={unauthenticatedLoginTarget.to}
        state={
          unauthenticatedLoginTarget.returnPath
            ? { pathname: unauthenticatedLoginTarget.returnPath }
            : undefined
        }
      />
    ),
    children: [
      { index: true, element: <MainContent /> },
      ...LOGIN_PORTAL_SLUGS.flatMap((slug) => [
        { path: slug, element: <MainContent /> },
        {
          path: `${slug}/${INSPECTIONS_TOOLKIT_SEGMENT}`,
          element: <MainContent />,
        },
        {
          path: `${slug}/${ADMINISTRATION_TOOLKIT_SEGMENT}`,
          element: <MainContent />,
        },
      ]),
      { path: INSPECTIONS_TOOLKIT_SEGMENT, element: <MainContent /> },
      { path: ADMINISTRATION_TOOLKIT_SEGMENT, element: <MainContent /> },
      { path: 'survey-data-viewer', element: <MainContent /> },
      { path: '*', element: <Navigate to="/" /> },
    ],
  },
  {
    path: '/login',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Login /> },
      { path: 'parks', element: <Login /> },
      { path: 'housing', element: <Login /> },
      { path: 'mod', element: <Login /> },
      { path: 'airports', element: <Login /> },
      { path: '*', element: <Navigate to="/login" replace /> },
    ],
  },
  {
    path: '/register',
    element: <AuthLayout />,
    children: [
      { path: '/register', element: <Register /> },
      { path: '*', element: <Navigate to="/register" /> },
    ],
  },
];
};

const App: React.FC = (): JSX.Element => {
  const location = useLocation();
  const userActions = useUserActions();
  const navigate = useNavigate();
  const userToken = useAppSelector((state) => state.user.token);
  const [isLoggedIn, setIsLoggedIn] = useState(hasValidStoredSession);
  const routing = useRoutes(getRoutes(isLoggedIn, location.pathname));

  useLayoutEffect(() => {
    setIsLoggedIn(userActions.restoreUser());
  }, []);

 
  useEffect(() => {
    if (userToken && hasValidStoredSession()) {
      setIsLoggedIn(true);
      return;
    }
    if (!userToken && !hasValidStoredSession()) {
      setIsLoggedIn(false);
    }
  }, [userToken]);

  useInsertionEffect(() => {
    userActions.getUserTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncActiveSectorFromPathname(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const documentHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--vh', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', documentHeight);
    documentHeight();

    return () => window.removeEventListener('resize', documentHeight);
  }, []);

  useEffect(() => {
    let isRedirecting = false;

    const interceptor = instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          !isRedirecting &&
          (error?.response?.status === 401 || error?.response?.status === 403)
        ) {
          isRedirecting = true;
          setIsLoggedIn(false);
          userActions.logout();
          isRedirecting = false;
        }
        return Promise.reject(error);
      },
    );

    return () => {
      instance.interceptors.response.eject(interceptor);
    };
  }, [userActions, navigate]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const stillLoggedIn = userActions.restoreUser();
      setIsLoggedIn(stillLoggedIn);
      if (!stillLoggedIn) {
        navigate(getLogoutLoginPath(location.pathname));
      }
    };

    const intervalId = setInterval(checkTokenExpiration, 1800000); // Check every 30 minutes

    return () => clearInterval(intervalId);
  }, [userActions, navigate, location.pathname]);

  return <>{routing}</>;
};

export default App;
